import React from 'react'
import { FocusTrap } from '@mui/base'
import { useCompanies, VehicleContext } from 'contexts'
import { UniversalVehicle } from 'models/universalData'
import { entityDisplayName } from 'utils/helpers'
import { useGetCompanyById } from 'utils/hooks/queries/useGetCompanyById'
import VehicleSheetSectionCarousel from './VehicleSheetSectionCarousel'
import VehicleSheetSectionCondition from './VehicleSheetSectionCondition/VehicleSheetSectionCondition'
import VehicleSheetSectionCharacteristics from './VehicleSheetSectionCharacteristics'
import VehicleSheetSectionInternalAdmin from './VehicleSheetSectionInternalAdmin/VehicleSheetModalSectionInternalAdmin'
import VehicleSheetSectionModelInfo from './VehicleSheetSectionModelInfo/VehicleSheetSectionModelInfo'
import './style.css'

const VehicleSheet = ({
  vehicle,
  isCarouselFull,
  setIsCarouselFull,
}: {
  vehicle: UniversalVehicle
  isCarouselFull: boolean
  setIsCarouselFull: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { parentCompanies, childrenCompanies, orphanCompanies } = useCompanies()

  const companyFromContext = React.useMemo(() => {
    if (!vehicle?.entityId) return null
    const allCompanies = [...parentCompanies, ...childrenCompanies, ...orphanCompanies]
    return allCompanies.find((company) => company.id === vehicle.entityId) ?? null
  }, [childrenCompanies, orphanCompanies, parentCompanies, vehicle?.entityId])

  const companyQuery = useGetCompanyById(vehicle?.entityId, {
    enabled: Boolean(vehicle?.entityId && !companyFromContext),
  })

  const resolvedCompany = companyFromContext ?? companyQuery.data ?? null
  const name = resolvedCompany ? entityDisplayName(resolvedCompany) : vehicle.entityId

  const treatedVehicle = React.useMemo(() => ({ ...vehicle, entityId: name }), [vehicle, name])

  return (
    <VehicleContext.Provider value={treatedVehicle}>
      <FocusTrap open>
        <div
          tabIndex={-1}
          className="vehicle-sheet"
        >
          <header className="vehicle-sheet__top-grid">
            <section className="vehicle-sheet-card vehicle-sheet__identity">
              <h1 className="vehicle-sheet__section-title">Identification</h1>
              <VehicleSheetSectionInternalAdmin />
            </section>
            <section className="vehicle-sheet-card vehicle-sheet__characteristics">
              <h2 className="vehicle-sheet__section-title">Informations Véhicule</h2>
              <VehicleSheetSectionCharacteristics />
            </section>
          </header>

          <main className="vehicle-sheet__main">
            <div className="vehicle-sheet__visual-column">
              <section
                className="vehicle-sheet-card vehicle-sheet__gallery"
                aria-label="Photos du véhicule"
              >
                <VehicleSheetSectionCarousel
                  isCarouselFull={isCarouselFull}
                  setIsCarouselFull={setIsCarouselFull}
                />
              </section>
            </div>

            <section
              className="vehicle-sheet-card vehicle-sheet__details"
              aria-label="Équipements, observations vendeur et documents"
            >
              <VehicleSheetSectionModelInfo />
            </section>
          </main>

          <VehicleSheetSectionCondition />
        </div>
      </FocusTrap>
    </VehicleContext.Provider>
  )
}

export default VehicleSheet
